import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { Button, Modal, Popconfirm, Spin } from "antd";
import {
  fetchDocumentVersions,
  restoreDocumentVersion,
} from "../../service/documentVersions";

// The window behind the "Version N" badge: every kept version of the
// document, who saved it and when, a way to look at an old one, and a
// button to bring it back. Reverting never deletes anything — the file
// being replaced is kept and the result is a new, higher version — so the
// confirmation says so, and a mistaken revert is itself a revert away.

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid #eef0f3;
  &:last-child {
    border-bottom: none;
  }
`;

const Main = styled.div`
  min-width: 0;
`;

const Title = styled.div`
  font-weight: 600;
  color: #111827;
`;

const Meta = styled.div`
  margin-top: 2px;
  font-size: 12px;
  color: #6b7280;
`;

const CurrentTag = styled.span`
  margin-left: 8px;
  padding: 1px 8px;
  border-radius: 10px;
  background: #e8f1fb;
  color: #1d4f91;
  font-size: 11px;
  font-weight: 600;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const Note = styled.p`
  margin: 0 0 12px;
  padding: 8px 10px;
  border-radius: 6px;
  background: #fff7e6;
  color: #7a4b00;
  font-size: 13px;
`;

export const formatVersionSize = (bytes) => {
  if (!Number.isFinite(bytes) || bytes < 0) return null;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const versionTitle = (entry) => {
  if (entry.is_original) return "Original";
  return `Version ${entry.version}`;
};

const versionDetail = (entry) => {
  const parts = [];
  if (entry.saved_by) parts.push(`Saved by ${entry.saved_by}`);
  if (entry.saved_at) {
    const when = new Date(entry.saved_at);
    if (!Number.isNaN(when.getTime())) parts.push(when.toLocaleString());
  }
  const size = formatVersionSize(entry.size);
  if (size) parts.push(size);
  if (entry.restored_from !== null && entry.restored_from !== undefined) {
    parts.push(`Restored from Version ${entry.restored_from}`);
  }
  return parts.join(" · ");
};

const DocumentVersionHistoryModal = ({
  open,
  documentId,
  documentName,
  onClose,
  onRestored,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [restoringVersion, setRestoringVersion] = useState(null);

  const load = useCallback(async () => {
    if (!documentId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await fetchDocumentVersions(documentId);
      setData(response?.data || null);
    } catch (err) {
      setError(
        err?.response?.data?.detail || "Could not load the version history."
      );
    } finally {
      setLoading(false);
    }
  }, [documentId]);

  useEffect(() => {
    if (open) {
      load();
    } else {
      setData(null);
      setError(null);
    }
  }, [open, load]);

  const handleRestore = async (entry) => {
    setRestoringVersion(entry.version);
    setError(null);
    try {
      const response = await restoreDocumentVersion(documentId, entry.version);
      if (onRestored) await onRestored(response?.data);
      onClose();
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          `Could not restore ${versionTitle(entry)}.`
      );
    } finally {
      setRestoringVersion(null);
    }
  };

  const versions = data?.versions || [];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      title={documentName ? `Version history · ${documentName}` : "Version history"}
      width={620}
      destroyOnClose
      data-testid="document-version-history-modal"
    >
      {data?.note && <Note>{data.note}</Note>}
      {error && (
        <Note role="alert" style={{ background: "#fdecea", color: "#8a1c14" }}>
          {error}
        </Note>
      )}
      {loading && !data ? (
        <div style={{ textAlign: "center", padding: 24 }}>
          <Spin />
        </div>
      ) : versions.length === 0 && !error ? (
        <Meta>No earlier versions have been kept yet.</Meta>
      ) : (
        versions.map((entry) => (
          <Row key={entry.version} data-testid={`version-row-${entry.version}`}>
            <Main>
              <Title>
                {versionTitle(entry)}
                {entry.is_current && <CurrentTag>Current</CurrentTag>}
              </Title>
              <Meta>{versionDetail(entry)}</Meta>
            </Main>
            <Actions>
              {entry.preview_url && (
                <Button
                  type="link"
                  size="small"
                  href={entry.preview_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Preview
                </Button>
              )}
              {entry.can_restore && (
                <Popconfirm
                  title={`Restore ${versionTitle(entry)}?`}
                  description="It becomes the newest version and replaces what is on screen. The current version is kept, so you can undo this."
                  okText="Restore"
                  cancelText="Cancel"
                  onConfirm={() => handleRestore(entry)}
                >
                  <Button
                    size="small"
                    loading={restoringVersion === entry.version}
                    disabled={restoringVersion !== null}
                  >
                    Restore
                  </Button>
                </Popconfirm>
              )}
            </Actions>
          </Row>
        ))
      )}
    </Modal>
  );
};

DocumentVersionHistoryModal.propTypes = {
  open: PropTypes.bool,
  documentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  documentName: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onRestored: PropTypes.func,
};

DocumentVersionHistoryModal.defaultProps = {
  open: false,
  documentId: null,
  documentName: "",
  onRestored: null,
};

export default DocumentVersionHistoryModal;
